import { useContext, useEffect, useState } from 'react';
import CartContext from '../contexts/CartContext';
import { API_ERRORS, getOffers } from '../api';
import { isCartPage } from '../dom';
import { getUniqueAddedOffers } from '../dom/ghost-products-mode';
import {
  addEligibleOffersToCart,
  debouncedAddEligibleOffersToCart,
  determineEligibleOffers,
  getAddedOffers,
  getJustAddedEligibleOffers,
  makeOfferMutationCallback,
  offerOverThreshold,
  openOnFirstVisit,
  setIconVisibility,
  SETTINGS_KEY,
  shouldOpen,
  visibleOffers as getVisibleOffers,
} from '../core/products';
import cartWatcher from '../dom/cart-watcher';
import ProductsContext from '../contexts/ProductsContext';
import PreviewContext from '../contexts/PreviewContext';
import Tracker from '../utils/tracking';
import {
  Cart,
  CollectionsInCart,
  OffersResponse,
  OfferUI,
  Product,
} from '../domain';
import AnimationContext from '../contexts/AnimationContext';
import isMobile from '../common/utils/isMobile';

export function useOffers(
  discountCode: string,
  setOpen: (isOpen: boolean) => void,
  tracker: Tracker,
): [OffersResponse, (discountCode: string) => Promise<void>] {
  const [data, setData] = useState<OffersResponse>({
    offers: [],
    settings: {},
    remainingOffers: 0,
  });
  const [updateCartCallback, setUpdateCartCallback] = useState<
    (discountCode: string) => Promise<void>
  >(async () => void 0);
  const { setCart, setCollectionsInCart } = useContext(CartContext);
  const { preloadProductsAndFilterOffers } = useContext(ProductsContext);
  const { onPreviewException, error: previewError } =
    useContext(PreviewContext);
  const animationContext = useContext(AnimationContext);

  useEffect(() => {
    async function fetchAndDoStuffWithData() {
      const data = await getOffers(window.Shopify.shop);

      if (data.error && data.error === API_ERRORS.PREVIEW_FORBIDDEN) {
        onPreviewException();
      }

      localStorage.setItem(SETTINGS_KEY, JSON.stringify(data.settings));
      const settings = data.settings;

      const { offers, products } = (await preloadProductsAndFilterOffers(
        data.offers,
      )) as { offers: OfferUI[]; products: Product[] };

      async function offersCallback(
        cart: Cart,
        collectionsInCart: CollectionsInCart,
      ) {
        const addedOffers = getAddedOffers().filter(
          (offer) => !!offer?.offerId,
        );
        setCart(cart);
        setCollectionsInCart(collectionsInCart);

        const justAddedEligibleOffers = getJustAddedEligibleOffers();

        for (const offer of offers) {
          if (
            offer.type === 'MultipleProductOffer' &&
            justAddedEligibleOffers.includes(offer.id) &&
            offer.shopify_product_handle &&
            isCartPage()
          ) {
            setOpen(true);
          }

          if (justAddedEligibleOffers.find((oid) => oid === offer.id)) {
            offer.justAdded = true;
          }
          const addedOffer = addedOffers.find(
            (addedOffer) => addedOffer.offerId === offer.id,
          );

          if (settings.gift_only_highest_gift) {
            offer.added = false;
          }

          if (addedOffer) {
            offer.added = true;
          }
        }

        const visibleOffers = getVisibleOffers(offers, settings);
        const uniqueAddedOffers = await getUniqueAddedOffers();
        const remainingOffers = visibleOffers.filter((visibleOffer) => {
          return !uniqueAddedOffers.find(
            (offer) => offer.offerId === visibleOffer.id,
          );
        }).length;

        setData({
          offers,
          settings: data.settings,
          remainingOffers,
        });

        let isIconVisible = data.settings?.show_giftbox_icon ?? true;
        if (isCartPage() && !isIconVisible) {
          const eligibleOffers = determineEligibleOffers(
            offers,
            cart,
            addedOffers,
            collectionsInCart,
            true,
          );

          const hasMultiOfferOverThreshold = eligibleOffers.some(
            (offer) =>
              offerOverThreshold(offer, cart, collectionsInCart) &&
              offer.type === 'MultipleProductOffer',
          );

          isIconVisible = hasMultiOfferOverThreshold;
        }

        setIconVisibility(isIconVisible ? 'visible' : 'hidden');

        if (shouldOpen(data.settings, justAddedEligibleOffers)) {
          animationContext.triggerWithAnimation?.({
            name: isMobile()
              ? 'auto_open_popup_mobile'
              : 'auto_open_popup_desktop',
            delayMsBefore: 100,
            delayMsAfter: 400,
            callback: () => {
              setOpen(isIconVisible);
              tracker.trackLauncherClickOpen(offers, cart);
            },
          });
        }

        if (data.settings?.show_giftbox_icon) {
          openOnFirstVisit(
            data.settings,
            setOpen,
            tracker,
            offers,
            cart,
            animationContext,
          );
        }
      }

      cartWatcher.rootCallback.inject(
        makeOfferMutationCallback({
          offers,
          settings: data.settings,
          products,
          offersCallback,
          discountCode,
        }),
      );

      cartWatcher.drawerCallback.inject(() => {
        return debouncedAddEligibleOffersToCart({
          offers,
          settings: data.settings,
          products,
          offersCallback,
          discountCode,
        });
      });

      cartWatcher.updateObserversFromSettings(data.settings);

      setUpdateCartCallback(
        () => async (discountCode: string) =>
          addEligibleOffersToCart({
            offers,
            settings: data.settings,
            products,
            offersCallback,
            discountCode,
          }),
      );
    }

    fetchAndDoStuffWithData();
  }, [previewError, discountCode]);

  return [data, updateCartCallback];
}
